import React, { useState, useEffect, useRef, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { IconButton, Icon, Menu, MenuItem } from "@material-ui/core";
import DateRangePicker from "@wojtekmaj/react-daterange-picker";
import { get, map, startCase, isEmpty, isArray } from "lodash";

import { getDateRanges } from "utils/get-date-range";
import PopupMenu from "components/items/popup-menu";
import { isIE } from "helpers/is-browser";
import { shouldSwapDates } from "utils/validators";

import BaseTextInput from "./base-text-input";

const useStyles = makeStyles(theme => ({
  inputContainer: {
    flex: "1 1 auto",
    "& .react-daterange-picker__calendar": {
      zIndex: "1000", // Fix input borders from showing above the popper
    },
    "& > .react-daterange-picker__wrapper": {
      width: "100%",
      border: "none",
      "& > .react-daterange-picker__range-divider": {
        width: 40,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 500,
      },
      "& > .react-daterange-picker__inputGroup": {
        pointerEvents: isIE ? "none" : "auto",
      },
      "& input": {
        minWidth: 20,
        textAlign: "center", // Fix spacing issues when dates are selected
        "&[name=year]": {
          minWidth: 20 * 2,
        },
      },
    },
  },
  selected: {
    backgroundColor: theme.palette.background.default,
  },
}));

const DefinedRangeItem = ({ label, onClick }) => (
  <MenuItem onClick={onClick}>
    {label}
  </MenuItem>
);

const DateRange = ({
  field,
  icon,
  disabled,
  form,
  definedRanges,
  onChange,
  helperText,
  renderExtraAction,
}) => {
  const classes = useStyles();
  const [selectedDateRange, setSelectedDateRange] = useState();
  // committedRange: null | [start, end] where end can be null for open-ended single-date commit
  const [committedRange, setCommittedRange] = useState(Array.isArray(field.value) ? field.value : null);
  // Holds a transient start date before user picks an end. We avoid putting this into committedRange to preserve focus.
  const pendingStartRef = useRef(Array.isArray(field.value) ? field.value[0] : null);
  const rootRef = useRef(null);

  // Sync external value (e.g. form reset)
  useEffect(() => {
    if (Array.isArray(field.value)) {
      setCommittedRange(field.value);
      pendingStartRef.current = field.value[0] || null;
    } else if (field.value == null) {
      setCommittedRange(null);
      pendingStartRef.current = null;
    }
  }, [field.value]);

  const { touched, errors } = form;

  // Commit either a full range or a single open-ended range. Handles swapping if reversed.
  const commit = useCallback((start, end) => {
    if (!start && !end) {
      setCommittedRange(null);
      form.setFieldValue(field.name, null, true);
      if (onChange) onChange(null);
      return;
    }
    let s = start;
    let e = end;
    if (s && e && shouldSwapDates([s, e])) {
      [s, e] = [e, s];
    }
    const value = e ? [s, e] : [s, null];
    setCommittedRange(value);
    form.setFieldValue(field.name, value, true);
    if (onChange) onChange(value);
  }, [form, field.name, onChange]);

  // On outside click commit a pending single start if user hasn't chosen an end.
  const handleOutside = useCallback(e => {
    if (!rootRef.current) return;
    if (!rootRef.current.contains(e.target)) {
      if (pendingStartRef.current && (!committedRange || !committedRange[1])) {
        // Commit single start when clicking away
        commit(pendingStartRef.current, committedRange ? committedRange[1] : null);
      }
    }
  }, [commit, committedRange]);

  useEffect(() => {
    document.addEventListener("mousedown", handleOutside, true);
    return () => document.removeEventListener("mousedown", handleOutside, true);
  }, [handleOutside]);

  // Commit pending single start when user focuses a different control (e.g. another date range field)
  useEffect(() => {
    const handleFocusIn = e => {
      if (!rootRef.current) return;
      if (pendingStartRef.current && (!committedRange || !committedRange[1]) && !rootRef.current.contains(e.target)) {
        commit(pendingStartRef.current, committedRange ? committedRange[1] : null);
        pendingStartRef.current = null;
      }
    };
    document.addEventListener("focusin", handleFocusIn, true);
    return () => document.removeEventListener("focusin", handleFocusIn, true);
  }, [commit, committedRange]);

  // Register a committer so the parent filter form can force pending single-date commits before submit (global Enter)
  // Global committer registration for form-level Enter submission.
  useEffect(() => {
    const committer = () => {
      if (pendingStartRef.current && (!committedRange || !committedRange[1])) {
        commit(pendingStartRef.current, committedRange ? committedRange[1] : null);
        pendingStartRef.current = null;
      }
    };
    if (!window.dateRangeCommitters) {
      Object.defineProperty(window, "dateRangeCommitters", { value: new Set(), configurable: true });
    }
    window.dateRangeCommitters.add(committer);
    return () => {
      if (window.dateRangeCommitters) {
        window.dateRangeCommitters.delete(committer);
      }
    };
  }, [commit, committedRange]);

  const DateInputComponent = () => (
    <DateRangePicker
      value={committedRange}
      className={classes.inputContainer}
      onChange={newDates => {
        if (newDates === null) {
          pendingStartRef.current = null;
          commit(null, null);
          return;
        }
        if (isArray(newDates)) {
          const [start, end] = newDates;
          // All cleared explicitly (library may pass [null,null])
          if (!start && !end) {
            pendingStartRef.current = null;
            commit(null, null);
            return;
          }
          // End only case: When tabbing from the start inputs to the end inputs, the picker can briefly emit [null, end]
          // even though the user DID enter a start. Preserve an existing pending or committed start if present.
          // Only treat as open-ended if there truly is no previously captured start.
          if (!start && end) {
            const existingStart = pendingStartRef.current || (committedRange ? committedRange[0] : null);
            if (existingStart) {
              commit(existingStart, end);
            } else {
              commit(null, end);
            }
            pendingStartRef.current = null;
            return;
          }
          // Start only (still selecting) -> hold pending
          if (start && !end) {
            pendingStartRef.current = start;
            return;
          }
          // Both selected -> commit full range
          if (start && end) {
            commit(start, end);
            pendingStartRef.current = null;
          }
        }
      }}
      format="d-M-yyyy" // temp workaround fix for leading 0 issue 'dd-MM-yyyy'
    />
  );

  const predefinedDateRanges = () => (
    <PopupMenu
      trigger={popupState => (
        <IconButton {...popupState} edge="end">
          <Icon>more_vert</Icon>
        </IconButton>
      )}
      content={({ close, ...other }) => (
        <Menu {...other}>
          {map(definedRanges, range => {
            if (!range) return null;
            const dateRanges = getDateRanges(range);
            const selected = range === selectedDateRange;
            return (
              <div key={range} className={selected ? classes.selected : ""}>
                <DefinedRangeItem
                  label={startCase(range)}
                  onClick={() => {
                    // Predefined always full
                    const [s, e] = dateRanges;
                    commit(s, e);
                    pendingStartRef.current = null;
                    setSelectedDateRange(range);
                    close();
                  }}
                />
              </div>
            );
          })}
        </Menu>
      )}
    />
  );

  return (
    <div ref={rootRef}>
      <BaseTextInput
        helperText={helperText}
        icon={icon || "date_range"}
        error={get(errors, field.name, null)}
        touched={get(touched, field.name, null)}
        disabled={disabled}
        inputComponent={DateInputComponent}
        value={committedRange}
        renderExtraAction={(
          <>
            {!isEmpty(definedRanges) && predefinedDateRanges()}
            {renderExtraAction && renderExtraAction}
          </>
        )}
      />
    </div>
  );
};

export default DateRange;
